import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { contactAPI } from '../services/api';
import { ArrowLeft, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';

export const AdminMessages = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    if (!user || !['admin', 'editor'].includes(user.role)) {
      navigate('/admin/login');
      return;
    }
    loadMessages();
  }, [user, navigate]);

  const loadMessages = async () => {
    try {
      const response = await contactAPI.getAll();
      if (response.success) {
        setMessages(response.messages);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Nachrichten');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'new': return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'read': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'replied': return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'new': return 'Neu';
      case 'read': return 'Gelesen';
      case 'replied': return 'Beantwortet';
      default: return status;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-[#1a1a1a] to-black">
      <div className="container mx-auto px-4 py-8">
        <Button
          onClick={() => navigate('/admin/dashboard')}
          variant="ghost"
          className="text-[#C4A55A] hover:text-[#D4B56A] mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zum Dashboard
        </Button>

        <h1 className="text-4xl font-bold text-white mb-8">Kontaktnachrichten</h1>

        {loading ? (
          <div className="text-center text-gray-400">Lädt Nachrichten...</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Messages List */}
            <div className="lg:col-span-1 space-y-4">
              {messages.length === 0 ? (
                <Card className="bg-[#1a1a1a] border-[#C4A55A]/20">
                  <CardContent className="p-6 text-center text-gray-400">
                    Keine Nachrichten vorhanden
                  </CardContent>
                </Card>
              ) : (
                messages.map((msg) => (
                  <Card
                    key={msg.id}
                    onClick={() => setSelectedMessage(msg)}
                    className={`bg-[#1a1a1a] border-[#C4A55A]/20 cursor-pointer transition-all duration-300 hover:border-[#C4A55A]/50 ${
                      selectedMessage?.id === msg.id ? 'border-[#C4A55A]' : ''
                    }`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-sm text-white mb-1">{msg.name}</CardTitle>
                          <CardDescription className="text-xs text-gray-400 flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {msg.email}
                          </CardDescription>
                        </div>
                        <Badge className={`${getStatusColor(msg.status)} border text-xs`}>
                          {getStatusText(msg.status)}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-sm text-gray-300 font-semibold mb-1">{msg.subject}</p>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="h-3 w-3" />
                        {new Date(msg.createdAt).toLocaleString('de-CH')}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Message Detail */}
            <div className="lg:col-span-2">
              {selectedMessage ? (
                <Card className="bg-[#1a1a1a] border-[#C4A55A]/20">
                  <CardHeader>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <CardTitle className="text-2xl text-white mb-2">{selectedMessage.subject}</CardTitle>
                        <CardDescription className="text-gray-400">
                          Von: {selectedMessage.name} ({selectedMessage.email})
                        </CardDescription>
                      </div>
                      <Badge className={`${getStatusColor(selectedMessage.status)} border`}>
                        {getStatusText(selectedMessage.status)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Clock className="h-4 w-4" />
                      {new Date(selectedMessage.createdAt).toLocaleString('de-CH', {
                        dateStyle: 'full',
                        timeStyle: 'short'
                      })}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-[#0a0a0a] border border-[#C4A55A]/20 rounded-lg p-6">
                      <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                        {selectedMessage.message}
                      </p>
                    </div>
                    <div className="mt-6 flex gap-3">
                      <Button
                        onClick={() => window.location.href = `mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                        className="bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black"
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        Per E-Mail antworten
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-[#1a1a1a] border-[#C4A55A]/20 h-full flex items-center justify-center">
                  <CardContent className="text-center text-gray-400 p-12">
                    <Mail className="h-16 w-16 mx-auto mb-4 text-[#C4A55A]/30" />
                    <p className="text-lg">Wählen Sie eine Nachricht aus, um Details anzuzeigen</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};